// ABOUTME: Cache policy for the per-user entities the schema says are one entity everywhere.
// ABOUTME: UserSettings/UserParameters carry the owner's userID as `id`, so one record serves every path.

import type { FieldReadFunction, Reference, TypePolicies } from "@apollo/client/cache"

type UserOwnedTypename = "UserSettings" | "UserParameters"

// Normalized, so the field holds a reference to the record rather than a copy.
type UserOwnedRead = FieldReadFunction<Reference | undefined>

/**
 * Reads a per-user entity off its normalized record instead of the query
 * field's own copy. A record written by any path — including `userInfo
 * { settings }` — answers a `…(request: {userID})` query for that same user.
 */
export const readUserOwnedEntity =
  (typename: UserOwnedTypename): UserOwnedRead =>
  (existing, { args, toReference }) => {
    if (existing) return existing

    const userID = (args?.request as { userID?: string } | undefined)?.userID
    // No userID means "whoever is signed in" — we can't name the record, so
    // let the query go out and normalize on the way back.
    if (!userID) return undefined

    return toReference({ __typename: typename, id: userID })
  }

/**
 * Keyed by `id`, which the schema defines as the owner's userID: "Same value
 * for every path returning this user's settings, so clients can normalize them
 * as one entity". The cross-user mixing that normalization is elsewhere kept
 * away from cannot happen when the key IS the user.
 */
export const userOwnedTypePolicies: TypePolicies = {
  UserSettings: { keyFields: ["id"] },
  UserParameters: { keyFields: ["id"] },
}

/** `Query` fields returning those entities, keyed and redirected to the record. */
export const userOwnedQueryFields = {
  userSettings: {
    keyArgs: ["request", ["userID"]],
    read: readUserOwnedEntity("UserSettings"),
  },
  userParameters: {
    keyArgs: ["request", ["userID"]],
    read: readUserOwnedEntity("UserParameters"),
  },
}
